import { TRAINING_BASE_DEFAULTS } from './constants';
import { DEFAULT_TRAINING_EMPLOYEES } from './data/employees';
import { TResolvedTraining, TTrainingMockConfig } from './types';

// The training mock is surfaced when the organization enables it AND names the
// document that carries the requirement, mirroring how the changeV2 mock is gated
// by its own `enabled` flag.
export const isTrainingMockVisible = (config?: TTrainingMockConfig): boolean =>
  Boolean(config?.enabled && config?.documentId);

// True when `documentId` is the configured training-trigger document. Compared as
// strings so a serialized ObjectId and a route param match.
export const isTrainingTriggerDocument = (config: TTrainingMockConfig | undefined, documentId: string): boolean =>
  isTrainingMockVisible(config) && String(config?.documentId) === String(documentId);

// Applies built-in defaults to the raw org config so every surface reads a complete
// record. Only ever called once `isTrainingMockVisible` has confirmed the identity
// fields, so the non-null assertions below are safe.
export const resolveTrainingConfig = (config: TTrainingMockConfig): TResolvedTraining => ({
  documentId: String(config.documentId),
  documentName: config.documentName?.trim() || TRAINING_BASE_DEFAULTS.documentName,
  revision: config.revision?.trim() || TRAINING_BASE_DEFAULTS.revision,
  reason: config.reason?.trim() || TRAINING_BASE_DEFAULTS.reason,
  trainingMethod: config.trainingMethod?.trim() || TRAINING_BASE_DEFAULTS.trainingMethod,
  targetFunctions:
    config.targetFunctions && config.targetFunctions.length > 0
      ? config.targetFunctions
      : [...TRAINING_BASE_DEFAULTS.targetFunctions],
  dueInDays: typeof config.dueInDays === 'number' ? config.dueInDays : TRAINING_BASE_DEFAULTS.dueInDays,
  employees: config.employees && config.employees.length > 0 ? config.employees : DEFAULT_TRAINING_EMPLOYEES,
});

// Default due date: today + `dueInDays`, as an ISO string (matching how the app's
// date fields store values).
export const getDefaultDueDate = (dueInDays: number): string => {
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + dueInDays);
  return dueDate.toISOString();
};
